import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { Grid, Header } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Location } from 'history';
import queryString from 'query-string';
import { format } from 'd3-format';

import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import Table from 'components/Table';
import { TableHeader } from 'components/Table/Table';

import { ClassProp } from 'helpers/types';
import { formatDate } from 'helpers/date';

import GET_MERCHANT_REVENUE_METRICS from './MerchantRevenueMetrics.queries';
import styles from './MerchantRevenueMetrics.styles';

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
  location: Location;
};

type RevenueMetric = {
  day: string;
  transactions: number;
  sales: number;
  aov: number;
  cashbackAmount: number;
  campaignFeeAmount: number;
};

type QueryResponse = {
  merchantRevenueMetrics: { result: RevenueMetric[] };
};

type ReportTitle = {
  [key: string]: string;
};

const reportTitle: ReportTitle = {
  [`Sam's Club`]: 'Membership Marketing',
  ['Costco']: 'Cashback Offers Metric',
  ['Walmart.com']: 'Cashback Offers Metric',
};

const tableHeader: TableHeader[] = [
  {
    source: ['day'],
    sortable: ['day'],
    label: 'Date',
    transform: (date: string) =>
      date !== '-' ? formatDate(date, 'MM/DD/YYYY') : 'Totals',
  },
  {
    source: ['transactions'],
    sortable: ['transactions'],
    justify: 'right',
    label: 'Transactions Count',
    transform: format(','),
  },
  {
    source: ['sales'],
    sortable: ['sales'],
    justify: 'right',
    label: 'Sales',
    transform: format('$,.2f'),
  },
  {
    source: ['aov'],
    sortable: ['aov'],
    justify: 'right',
    label: 'AOV',
    transform: format(',.2f'),
    calcMean: true,
  },
  {
    source: ['cashbackAmount'],
    sortable: ['cashbackAmount'],
    justify: 'right',
    label: 'Cashback',
    transform: format('$,.2f'),
  },
  {
    source: ['campaignFeeAmount'],
    sortable: ['campaignFeeAmount'],
    justify: 'right',
    label: 'Campaign Fee',
    transform: format('$,.2f'),
  },
];

const CSVFields = [
  {
    label: 'Date',
    value: ({ day }: RevenueMetric) =>
      day !== '-' ? formatDate(day, 'MM/DD/YYYY') : 'Totals',
  },
  {
    label: 'Transactions',
    value: ({ transactions }: RevenueMetric) => format(',')(transactions),
  },
  {
    label: 'Sales',
    value: ({ sales }: RevenueMetric) => format('$,.2f')(sales),
  },
  {
    label: 'AOV',
    value: ({ aov }: RevenueMetric) => format(',.2f')(aov),
  },
  {
    label: 'Cashback Amount',
    value: ({ cashbackAmount }: RevenueMetric) =>
      format('$,.2f')(cashbackAmount),
  },
  {
    label: 'Campaign Fee Amount',
    value: ({ campaignFeeAmount }: RevenueMetric) =>
      format('$,.2f')(campaignFeeAmount),
  },
];

const renderReport = (
  params: ReportParams,
  classes: ClassProp<ReturnType<typeof styles>>,
  title: string,
) => ({ merchantRevenueMetrics: { result } }: QueryResponse) => (
  <ReportBase header={title} params={params}>
    <Grid.Row className={classes.charts} centered>
      <Grid.Column width={16} className={classes.gridTable}>
        <Header className={classes.tableHeader} as="h3">
          {title}
        </Header>

        <Header className={classes.tableHeader} as="h4">
          All totals include both cleared and pending transactions
        </Header>

        <Table
          sortDefaultColumn={{ name: 'Date', sortable: ['day'] }}
          tableHeader={tableHeader}
          data={result}
          splitEveryArg={30}
          showTotals
          exportData
          csvFields={CSVFields}
        />
      </Grid.Column>
    </Grid.Row>
  </ReportBase>
);

const MerchantRevenueMetrics: FunctionComponent<Props> = ({
  classes,
  location,
}) => {
  const params = queryString.parse(location.search) as ReportParams;
  const merchantName = params.merchantName as string;

  return (
    <Query
      query={GET_MERCHANT_REVENUE_METRICS}
      reportName="Membership Marketing"
      batchKey="result"
      propName="merchantRevenueMetrics"
      variables={{
        params,
      }}
      render={renderReport(params, classes, reportTitle[merchantName])}
    />
  );
};

export default compose(
  withStyles(styles as any),
  withRouter,
)(MerchantRevenueMetrics as any);
